import { useCallback, useEffect, useRef, useState } from 'react';
import { createComment, deleteComment, listComments, replyComment, resolveComment, type CommentAnchor, type CommentPerson, type CommentThread, type FileComment } from '../../services/comments';

/** While the Comments panel is out, the threads are read again this often (one call, all threads). */
const REFRESH_MS = 30_000;

/**
 * A file's comment threads (W5-AR2): read when the panel opens or a pin is wanted, kept for the
 * visit, read again every half minute while the panel is out. Every write shows at once and is
 * put back, with a sentence, if the server says no.
 */
export function useComments(documentId: string, opts: { me: CommentPerson | null; active: boolean; onError: (message: string) => void }) {
  const [threads, setThreads] = useState<CommentThread[] | null>(null);
  const [canComment, setCanComment] = useState(false);
  const [failed, setFailed] = useState(false);
  const alive = useRef(true);
  const asking = useRef<Promise<void> | null>(null);
  const onError = useRef(opts.onError);
  onError.current = opts.onError;

  useEffect(() => {
    alive.current = true;
    setThreads(null);
    return () => {
      alive.current = false;
    };
  }, [documentId]);

  const refresh = useCallback((): Promise<void> => {
    if (asking.current) return asking.current;
    const asked = listComments(documentId)
      .then((answer) => {
        if (!alive.current) return;
        setThreads(answer.threads);
        setCanComment(answer.canComment);
        setFailed(false);
      })
      .catch(() => {
        if (alive.current) setFailed(true);
      })
      .finally(() => {
        asking.current = null;
      });
    asking.current = asked;
    return asked;
  }, [documentId]);

  useEffect(() => {
    if (!opts.active) return;
    void refresh();
    const timer = window.setInterval(() => void refresh(), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [opts.active, refresh]);

  const change = (fn: (now: CommentThread[]) => CommentThread[]) => setThreads((now) => fn(now ?? []));
  const fail = (error: unknown, fallback: string) => onError.current((error as Error)?.message || fallback);
  const author = opts.me ?? { id: 'me', name: null, avatarUrl: null };

  /** A new thread at a pin. Answers it once stored, null if refused. */
  const create = async (anchor: CommentAnchor, body: string, mentions: readonly string[]): Promise<CommentThread | null> => {
    const temp: CommentThread = { id: `draft-${Date.now()}`, body, author, mentions, createdAt: new Date().toISOString(), mine: true, anchor, resolved: false, resolvedAt: null, replies: [] };
    change((now) => [...now, temp]);
    try {
      const made = await createComment(documentId, anchor, body, mentions);
      change((now) => now.map((one) => (one.id === temp.id ? made : one)));
      return made;
    } catch (error) {
      change((now) => now.filter((one) => one.id !== temp.id));
      fail(error, 'That comment could not be posted.');
      return null;
    }
  };

  const reply = async (threadId: string, body: string, mentions: readonly string[]): Promise<boolean> => {
    const temp: FileComment = { id: `draft-${Date.now()}`, body, author, mentions, createdAt: new Date().toISOString(), mine: true };
    change((now) => now.map((one) => (one.id === threadId ? { ...one, replies: [...one.replies, temp] } : one)));
    try {
      const made = await replyComment(documentId, threadId, body, mentions);
      change((now) => now.map((one) => (one.id === threadId ? { ...one, replies: one.replies.map((r) => (r.id === temp.id ? made : r)) } : one)));
      return true;
    } catch (error) {
      change((now) => now.map((one) => (one.id === threadId ? { ...one, replies: one.replies.filter((r) => r.id !== temp.id) } : one)));
      fail(error, 'That reply could not be posted.');
      return false;
    }
  };

  const resolve = async (threadId: string, resolved: boolean) => {
    change((now) => now.map((one) => (one.id === threadId ? { ...one, resolved } : one)));
    try {
      await resolveComment(documentId, threadId, resolved);
    } catch (error) {
      change((now) => now.map((one) => (one.id === threadId ? { ...one, resolved: !resolved } : one)));
      fail(error, resolved ? 'That thread could not be resolved.' : 'That thread could not be opened again.');
    }
  };

  const remove = async (threadId: string, commentId: string) => {
    const before = threads;
    change((now) =>
      commentId === threadId ? now.filter((one) => one.id !== threadId) : now.map((one) => (one.id === threadId ? { ...one, replies: one.replies.filter((r) => r.id !== commentId) } : one)),
    );
    try {
      await deleteComment(documentId, commentId);
    } catch (error) {
      setThreads(before);
      fail(error, 'That comment could not be deleted.');
    }
  };

  return { threads, canComment, failed, refresh, create, reply, resolve, remove };
}
